import * as React from "react";
import { format, isAfter, isSameDay } from "date-fns";
import { CalendarIcon } from "lucide-react";
import type { DateRange } from "react-day-picker";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export interface DateRangePreset {
  label: string;
  getRange: () => DateRange;
}

function startOfToday(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function daysAgo(n: number): Date {
  const d = startOfToday();
  d.setDate(d.getDate() - n);
  return d;
}

// Default preset list — covers the common single-day asks (Today/Yesterday)
// as well as the common trend windows, so most visits never need to touch
// the calendar at all. Pass `presets={[]}` to hide this column entirely.
export const DEFAULT_DATE_RANGE_PRESETS: DateRangePreset[] = [
  {
    label: "Today",
    getRange: () => ({ from: startOfToday(), to: startOfToday() }),
  },
  {
    label: "Yesterday",
    getRange: () => ({ from: daysAgo(1), to: daysAgo(1) }),
  },
  {
    label: "Last 7 days",
    getRange: () => ({ from: daysAgo(6), to: startOfToday() }),
  },
  {
    label: "Last 30 days",
    getRange: () => ({ from: daysAgo(29), to: startOfToday() }),
  },
];

interface DateRangePickerProps {
  range?: DateRange;
  onRangeChange?: (range: DateRange | undefined) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  /**
   * Widest span selectable via the calendar, in days inclusive (from -> to).
   * Keeps whatever query/chart this feeds bounded — same idea as disabling
   * future dates below. Optional; omit for no cap. Presets aren't affected
   * by this (they're fixed, known-good spans).
   */
  maxRangeDays?: number;
  /** Quick-pick buttons shown beside the calendar. Pass [] to hide them. */
  presets?: DateRangePreset[];
}

function formatRange(range?: DateRange): string {
  if (!range?.from) return "";
  if (!range.to || range.to.getTime() === range.from.getTime()) {
    return format(range.from, "MMM d, yyyy");
  }
  const sameYear = range.from.getFullYear() === range.to.getFullYear();
  const fromFmt = sameYear ? "MMM d" : "MMM d, yyyy";
  return `${format(range.from, fromFmt)} – ${format(range.to, "MMM d, yyyy")}`;
}

// If the committed range exactly matches one of the presets, show that
// preset's label on the trigger ("Last 7 days") instead of spelling out the
// dates — matches how every reference picker (Vercel/GA4/Stripe-style)
// labels its trigger.
function matchingPresetLabel(
  range: DateRange | undefined,
  presets: DateRangePreset[],
): string | null {
  if (!range?.from || !range?.to) return null;
  for (const preset of presets) {
    const r = preset.getRange();
    if (
      r.from &&
      r.to &&
      isSameDay(r.from, range.from) &&
      isSameDay(r.to, range.to)
    ) {
      return preset.label;
    }
  }
  return null;
}

export function DateRangePicker({
  range,
  onRangeChange,
  placeholder = "Pick a date range",
  className,
  disabled = false,
  maxRangeDays,
  presets = DEFAULT_DATE_RANGE_PRESETS,
}: DateRangePickerProps) {
  const [open, setOpen] = React.useState(false);

  // A local, resettable copy of the in-progress calendar selection —
  // deliberately NOT the same thing as the committed `range` prop, and
  // deliberately NOT auto-committed the moment it becomes a complete range.
  //
  // 2026-09-14: this now opens PRE-FILLED with the committed `range` (see
  // onOpenChange below) instead of always blank, so reopening the picker
  // shows the existing selection highlighted rather than looking like
  // nothing is selected. That reintroduces a react-day-picker quirk this
  // component used to dodge by staying blank: range mode's addToRange (see
  // addToRange.js) treats an ALREADY-COMPLETE `selected` range as something
  // to nudge one endpoint of, not replace outright — a single click would
  // just swap in whichever old endpoint (from the previous committed range)
  // is nearer, instead of starting a new pick. `startedFreshRef` below is
  // what intercepts exactly that first click and forces it to start a
  // brand-new single-day pick instead; every click after that is a normal,
  // real in-progress range and is fine to trust as-is.
  //
  // Why not auto-close as soon as `draft` becomes a complete range: from a
  // blank slate, react-day-picker's default `min` is 0, so a SINGLE click
  // alone already produces a complete one-day range (`{from: day, to:
  // day}` — see addToRange's empty-range branch). If we closed on any
  // complete range, one click would always finish the interaction, and a
  // second click to extend it into a real multi-day range would never get
  // the chance to happen — the exact "clicking once = it books it,
  // clicking a real range is now impossible" bug this app has already hit
  // once. The explicit Apply button below is what actually commits;
  // clicking a second day just extends `draft` (a complete range is a
  // perfectly normal thing to keep clicking on top of).
  const [draft, setDraft] = React.useState<DateRange | undefined>(undefined);

  // Tracks whether the FIRST click since the popover opened has happened
  // yet. Needed now that `draft` opens pre-filled with the committed
  // `range` (see onOpenChange below) instead of blank — react-day-picker's
  // addToRange treats an already-complete `selected` range as something to
  // nudge one endpoint of, not replace, so without this the first click
  // after reopening would silently drag the nearer of the OLD from/to
  // toward wherever was clicked instead of starting a new pick. Set back to
  // false every time the popover opens; flipped true the first time
  // handleSelect runs, after which trusting react-day-picker's own
  // in-progress-range logic is exactly what we want.
  const startedFreshRef = React.useRef(false);

  // Disable strictly-future days (activity can't be dated in the future;
  // today itself stays selectable) and, once a first day is picked,
  // anything more than maxRangeDays away from it in either direction.
  const isDisabledDay = React.useCallback(
    (day: Date) => {
      if (isAfter(day, new Date())) return true;
      if (maxRangeDays && draft?.from) {
        const msPerDay = 24 * 60 * 60 * 1000;
        const diffDays =
          Math.abs(day.getTime() - draft.from.getTime()) / msPerDay;
        if (diffDays > maxRangeDays - 1) return true;
      }
      return false;
    },
    [draft, maxRangeDays],
  );

  const commit = (next: DateRange | undefined) => {
    onRangeChange?.(next);
    setOpen(false);
  };

  // See startedFreshRef's comment above for why the first call here is
  // special-cased instead of just forwarding straight to setDraft.
  const handleSelect = (
    nextRange: DateRange | undefined,
    triggerDate: Date,
  ) => {
    if (!startedFreshRef.current) {
      startedFreshRef.current = true;
      setDraft({ from: triggerDate, to: undefined });
      return;
    }
    setDraft(nextRange);
  };

  const presetLabel = matchingPresetLabel(range, presets);
  const triggerLabel = presetLabel ?? (range?.from ? formatRange(range) : null);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          // Pre-fill with the last COMMITTED range so it shows highlighted
          // on open instead of a blank calendar — handleSelect above is
          // what makes the first click still start a fresh pick rather
          // than nudging one of these old endpoints.
          setDraft(range);
          startedFreshRef.current = false;
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className={cn(
            "min-h-[44px] w-full justify-start text-left font-normal sm:w-auto",
            !triggerLabel && "text-muted-foreground",
            className,
          )}
        >
          <CalendarIcon />
          {triggerLabel ?? <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      {/* collisionPadding: this popover is wider than the plain single-day
          <DatePicker> (presets column + calendar), so Radix's collision
          avoidance was shoving it flush against the viewport edge with 0
          gap once it didn't fit fully on-screen — the default
          collisionPadding is 0. 5px keeps a small breathing gap instead. */}
      <PopoverContent
        className="w-auto p-0"
        align="start"
        collisionPadding={10}
      >
        <div className="flex flex-col sm:flex-row">
          {presets.length > 0 && (
            <div className="flex flex-row gap-1 overflow-x-auto border-b p-2 pt-4 sm:w-40 sm:flex-col sm:overflow-visible sm:border-b-0 sm:border-r sm:pt-6">
              {presets.map((preset) => (
                <Button
                  key={preset.label}
                  type="button"
                  variant="ghost"
                  size="sm"
                  // min-h-[44px], not min-h-9 (36px) — same 44x44px touch
                  // target rule the trigger button above already follows.
                  className="min-h-[44px] shrink-0 justify-start whitespace-nowrap"
                  onClick={() => commit(preset.getRange())}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
          )}
          <div className="p-3">
            <Calendar
              mode="range"
              selected={draft}
              defaultMonth={draft?.from ?? range?.from}
              disabled={isDisabledDay}
              onSelect={handleSelect}
            />
            <div className="flex items-center justify-between gap-2 border-t border-border pt-3">
              {/* Live preview of the in-progress draft — otherwise the only
                  feedback before Apply is the highlighted calendar cells
                  themselves, and there's no text confirmation of what's
                  about to be committed. */}
              <span className="text-sm text-muted-foreground">
                {draft?.from
                  ? draft.to && !isSameDay(draft.from, draft.to)
                    ? formatRange(draft)
                    : `${format(draft.from, "MMM d, yyyy")} – pick an end date`
                  : "Pick a start date"}
              </span>
              <div className="flex gap-2">
                {/* min-h-[44px] on both — same touch-target rule as the
                    presets and trigger button; size="sm"'s default h-9
                    (36px) is under it. */}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="min-h-[44px]"
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  className="min-h-[44px]"
                  disabled={!draft?.from}
                  onClick={() =>
                    draft?.from &&
                    commit({ from: draft.from, to: draft.to ?? draft.from })
                  }
                >
                  Apply
                </Button>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
